<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		:persistent="connectingToDataSource"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
		@keydown.enter="connectToDataSource"
	>
		<v-card class="sgnl-pb-0">
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>{{ dialogTitle }}
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="handleDismiss"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body">
				<v-form
					ref="dataSourceForm"
					v-model="formValid"
					class="stack"
				>
					<div class="input-group">
						<div class="field-label">Data Source</div>
						<v-select
							v-model="chosenDataSource"
							hide-details
							:items="dataSourceTypes"
							:disabled="modifySourceConfigRequest"
							@update:model-value="setupDataSourceConfig"
							placeholder="Select a Data Source"
						>
							<template v-slot:item="{ item, props }">
								<v-list-item
									v-bind="props"
									:disabled="item.raw.disabled"
									:style="item.raw.disabled ? { color: 'var(--sgnl-default-text-disabled)' } : {}"
								/>
							</template>
						</v-select>
					</div>
					<v-divider v-if="chosenDataSource" />
					<div
						v-if="chosenDataSource == 'Bucket'"
						class="bucket-config"
					>
						<div>
							<div class="input-group">
								<div class="field-label">AWS Account ID</div>
								<v-text-field
									hint="Your 12-digit Account ID can be found in the top right-hand corner of your AWS console."
									v-model="dataSourceConfig.accountID"
									maxlength="12"
									:rules="[rules.required, rules.awsS3.accountIDNumeric, rules.awsS3.accountIDLength]"
									:disabled="modifySourceConfigRequest"
								/>
							</div>
							<div class="input-group">
								<div class="field-label">S3 Bucket Name</div>
								<v-text-field
									placeholder="bucket-name"
									v-model="dataSourceConfig.bucketName"
									:rules="[
										rules.required,
										rules.awsS3.bucketNameNoARN,
										rules.awsS3.bucketNameStartValid,
										rules.awsS3.bucketNameEndValid,
										rules.awsS3.bucketNameLengthValid,
									]"
									:disabled="modifySourceConfigRequest"
								/>
							</div>
							<div class="input-group">
								<div class="field-label">Permissions</div>
								<div class="sgnl-flex sgnl-flex-column">
									<v-checkbox
										v-model="dataSourceConfig.readPermission"
										label="Read"
										hide-details
									/>
									<v-checkbox
										v-model="dataSourceConfig.writePermission"
										label="Write"
										hide-details
									/>
								</div>
							</div>
						</div>
						<div>
							<v-btn
								variant="outlined"
								:disabled="!dataSourceConfig.bucketName"
								@click="copyIAMPolicyToClipboard"
							>
								<span>Copy Suggested IAM Policy</span>
								<Copy16 />
							</v-btn>
							<v-snackbar
								v-model="showIAMCopyNotification"
								absolute
								class="snackbar-success"
							>
								<div class="snackbar-icon-container snackbar-success">
									<CheckmarkFilled16 />
								</div>
								Copied AWS IAM policy to clipboard!
							</v-snackbar>
						</div>
						<div class="bucket-note">
							<div class="sgnl-caption-regular">
								Please make sure to grant <em>Signaloid Cloud</em> access to your S3 bucket by updating
								its IAM policy in the
								<a
									target="_blank"
									:href="`https://s3.console.aws.amazon.com/s3/bucket/${dataSourceConfig.bucketName}/property/policy/edit`"
									class="sgnl-link"
								>
									AWS console <ArrowUpRight16 /> </a
								>.
							</div>
							<div class="sgnl-footnote-regular">
								<sup>1</sup>
								The policy is provided "as is", without warranty of any kind, express or implied,
								including but not limited to the warranties of merchantability, fitness for a particular
								purpose and noninfringement. In no event shall Signaloid be liable for any claim,
								damages or other liability, whether in an action of contract, tort or otherwise, arising
								from, out of or in connection with the software or the use or other dealings in the
								supplied policy.
							</div>
						</div>
					</div>
				</v-form>
				<v-alert
					v-if="alert.show"
					variant="outlined"
					:type="alert.type"
					border="start"
					class="sgnl-paragraph-s-regular"
				>
					{{ alert.text }}
				</v-alert>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="handleDismiss"
					>Cancel</v-btn
				>
				<LimitableActionButton
					variant="flat"
					color="primary"
					:disabled="!chosenDataSource"
					:tooltipText="modifySourceConfigRequest ? 'Update Data Source' : 'Connect Data Source'"
					data-test="connect-repo-button"
					:limits-to-check="tierLimitsToCheck"
					@valid-click="connectToDataSource"
					:loading="connectingToDataSource"
					:btn-text="modifySourceConfigRequest ? 'Update' : 'Connect'"
				/>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import Copy16 from "@carbon/icons-vue/es/copy/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import ArrowUpRight16 from "@carbon/icons-vue/es/arrow--up-right/16";

// Stores
import { mapState } from "pinia";
import { useDataSourcesStore } from "@/stores/dataSources";
import { useUserStore } from "@/stores/user";

// Utilities
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { monitoringCaptureError } from "@/plugins/monitoring";

// Components
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";

// Types
import { UserLimitsE } from "@/js/tierconfig";

type ComponentData = {
	formValid: boolean;
	dataSourceTypes: DataSourceTypeListItem[];
	dataSourceConfig: DataSourceConfig;
	connectingToDataSource: boolean;
	chosenDataSource: undefined | string;
	alert: Alert;
	modifySourceConfigRequest: boolean;
	showIAMCopyNotification: boolean;
};

type Alert = {
	show: boolean;
	text: string;
	type: "error" | "warning" | "info" | "success";
};

type DataSourceTypeListItem = {
	title: string;
	value: string;
	disabled?: boolean;
};

type DataSourceConfig = {
	sourceID?: string;
	sourceType: string;
	accountID: string;
	bucketName: string;
	readPermission: boolean;
	writePermission: boolean;
};

export default defineComponent({
	name: "DataSourceConfigDialog",
	components: { LimitableActionButton, Close16, Copy16, CheckmarkFilled16, ArrowUpRight16 },
	props: {
		showDialog: { type: Boolean, default: false },
		editSourceData: { type: Object as PropType<DataSourceConfig>, default: undefined },
	},
	emits: {
		"update:showDialog": (_data: boolean) => true,
		"bucket-config-updated": () => true,
		"bucket-connected": () => true,
	},
	data: (): ComponentData => ({
		formValid: false,
		dataSourceTypes: [
			{ title: "S3 Bucket", value: "Bucket" },
			{ title: "Signaloid D0", value: "D0", disabled: true },
			{ title: "Signaloid Sensor Gateway", value: "Gateway", disabled: true },
			{ title: "API", value: "API", disabled: true },
		],
		// @ts-ignore FIXME: add correct DataSourceConfig type
		dataSourceConfig: {},
		connectingToDataSource: false,
		chosenDataSource: undefined,
		alert: { show: false, text: "", type: "warning" },
		modifySourceConfigRequest: false,
		showIAMCopyNotification: false,
	}),
	setup() {
		const userStore = useUserStore();
		const dataSourcesStore = useDataSourcesStore();
		return { userStore, dataSourcesStore, UserLimitsE };
	},
	methods: {
		charIsAlphaNumeric(value: string | number) {
			if (typeof value == "string") {
				return value.toLowerCase().charCodeAt(0) >= 97 && value.toLowerCase().charCodeAt(0) <= 122;
			} else if (typeof value == "number") {
				return isFinite(value);
			} else {
				return false;
			}
		},
		handleDismiss() {
			if (this.connectingToDataSource) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			this.$emit("update:showDialog", false);
			this.chosenDataSource = "";
			this.alert = { show: false, text: "", type: "warning" };
		},
		async connectToDataSource() {
			this.alert = { show: false, text: "", type: "warning" };
			//@ts-ignore
			this.$refs.dataSourceForm.validate();
			if (this.formValid) {
				if (this.dataSourceConfig.sourceType == "Bucket") {
					this.connectingToDataSource = true;
					try {
						if (this.modifySourceConfigRequest && this.dataSourceConfig.sourceID) {
							await sdk.buckets.update(this.dataSourceConfig.sourceID, {
								Read: this.dataSourceConfig.readPermission,
								Write: this.dataSourceConfig.writePermission,
							});
							this.$emit("bucket-config-updated");
						} else {
							await sdk.buckets.create({
								Name: this.dataSourceConfig.bucketName,
								Account: this.dataSourceConfig.accountID,
								Read: this.dataSourceConfig.readPermission,
								Write: this.dataSourceConfig.writePermission,
							});
							this.$emit("bucket-connected");
							this.$posthog?.capture("bucket_connected");
						}
						this.closeDialog();
					} catch (error) {
						monitoringCaptureError(error, "Connect to data source");

						if (error instanceof SdkError) {
							const detailsBody = error.details as { message?: string; error?: string } | undefined;
							this.alert = {
								show: true,
								text:
									detailsBody?.message ||
									detailsBody?.error ||
									"An error occurred while connecting to the data source.",
								type: error.code === ERROR_CODES.API_FORBIDDEN ? "warning" : "error",
							};
						}
					} finally {
						this.connectingToDataSource = false;
					}
				}
			}
		},
		setupDataSourceConfig(sourceType: string) {
			switch (sourceType) {
				case "Bucket":
					this.dataSourceConfig = {
						sourceType: sourceType,
						accountID: "",
						bucketName: "",
						readPermission: true,
						writePermission: false,
					};
					break;
				default:
					// @ts-ignore FIXME: add correct DataSourceConfig type
					this.dataSourceConfig = {};
					break;
			}
		},
		copyIAMPolicyToClipboard() {
			navigator.clipboard.writeText(JSON.stringify(this.iamPolicy, null, 4));
			this.showIAMCopyNotification = true;
		},
	},
	computed: {
		...mapState(useDataSourcesStore, {
			bucketList: "bucketList",
		}),
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		tierLimitsToCheck() {
			switch (this.dataSourceConfig.sourceType) {
				case "Bucket":
					return [UserLimitsE.BucketCount];
				default:
					return [];
			}
		},

		// @ts-ignore FIXME: add correct types
		rules(): any {
			return {
				required: (value) => !!value || "Required.",
				counter: (value) => value.length <= 50 || "Max 50 characters",
				awsS3: {
					accountIDLength: (value) => value.length == 12 || "Account ID must be 12 characters long",
					accountIDNumeric: (value) => !isNaN(value) || "Account ID must be a number",
					bucketNameNoARN: (value) =>
						!value.toLowerCase().startsWith("arn") || "Please enter the S3 Bucket name not ARN",
					bucketNameLengthValid: (value) =>
						(value.length >= 3 && value.length <= 63) ||
						"The S3 Bucket name must be between 3 and 64 characters long",
					bucketNameStartValid: (value) =>
						(this.charIsAlphaNumeric(value.charAt(0)) && !value.startsWith("xn--")) ||
						"The S3 Bucket name must start with a letter or a number and also not 'xn--'",
					bucketNameEndValid: (value) =>
						(this.charIsAlphaNumeric(value.charAt(value.length - 1)) && !value.endsWith("-s3alias")) ||
						"The S3 Bucket name must end with a letter or a number and also not '-s3alias'",
					// bucketNameCharactersvalid: (value) => //TODO: || "Please enter a valid S3 Bucket Name",
				},
			};
		},
		// @ts-ignore FIXME: add correct types
		iamPolicy(): any {
			const iamPolicy = {
				Version: "2012-10-17",
				Statement: [
					{
						Sid: "SignaloidCloudBucketPermissionsList",
						Effect: "Allow",
						Principal: {
							AWS: `arn:aws:iam::${import.meta.env.VITE_ENV_AWS_ACCOUNT_ID}:root`,
						},
						Action: ["s3:GetBucketLocation", "s3:ListBucket"],
						Resource: `arn:aws:s3:::${this.dataSourceConfig.bucketName}`,
					},
				],
			};

			if (this.dataSourceConfig.readPermission) {
				iamPolicy.Statement.push({
					Sid: "SignaloidCloudBucketPermissionsRead",
					Effect: "Allow",
					Principal: {
						AWS: `arn:aws:iam::${import.meta.env.VITE_ENV_AWS_ACCOUNT_ID}:root`,
					},
					Action: ["s3:GetObject"],
					Resource: `arn:aws:s3:::${this.dataSourceConfig.bucketName}/*`,
				});
			}

			if (this.dataSourceConfig.writePermission) {
				iamPolicy.Statement.push({
					Sid: "SignaloidCloudBucketPermissionsWrite",
					Effect: "Allow",
					Principal: {
						AWS: `arn:aws:iam::${import.meta.env.VITE_ENV_AWS_ACCOUNT_ID}:root`,
					},
					Action: ["s3:PutObject", "s3:DeleteObject"],
					Resource: `arn:aws:s3:::${this.dataSourceConfig.bucketName}/*`,
				});
			}

			return iamPolicy;
		},
		dialogTitle(): string {
			return this.modifySourceConfigRequest ? "Modify Data Source" : "Add a Data Source";
		},
	},
	async created() {
		await this.userStore.getCurrentUserTierDetails();
		await this.dataSourcesStore.fetchBucketList();
	},
	mounted() {
		if (this.editSourceData) {
			this.modifySourceConfigRequest = true;
			this.chosenDataSource = this.editSourceData.sourceType;
			this.dataSourceConfig = this.editSourceData;
		}
	},
});
</script>

<style scoped>
.bucket-config {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.bucket-note {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}
</style>
